import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Smartphone, Building2, Check, Globe } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";

export default function PublicPay() {
    const { linkCode } = useParams();
    const nav = useNavigate();
    const [info, setInfo] = useState(null);
    // Pre-fill from query string so embeds can pass ?amount=1500&username=u123.
    const params = new URLSearchParams(window.location.search);
    const [amount, setAmount] = useState(params.get("amount") || "");
    const [username, setUsername] = useState(params.get("username") || "");
    const [methods, setMethods] = useState(null);
    const [selected, setSelected] = useState(null);
    // Customer-selected website (the chooser is hidden when there's 0 or 1
    // website configured). Initialised after the first /pay/{code} response
    // resolves — applying suggested_website_id > default_website_id > null.
    const [websiteId, setWebsiteId] = useState(null);
    // Tracks whether the customer has manually chosen a website. Once true,
    // the auto-suggest (which fires when username matches a prior payment)
    // STOPS overriding their pick — annoying-to-debug bug otherwise.
    const [websiteTouched, setWebsiteTouched] = useState(false);
    // Set when the most recent auto-select call returned a suggestion that
    // matched a prior payment for this username. Drives the friendly
    // "Auto-selected based on your previous payments" banner so the
    // customer understands why a specific website is highlighted.
    const [autoSelectedFromHistory, setAutoSelectedFromHistory] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get(`/public/pay/${linkCode}`).then((r) => {
            setInfo(r.data);
            // Seed initial pick: suggested > default > first website (when
            // only one is configured; in that case we hide the chooser).
            const initial = r.data.suggested_website_id
                || r.data.default_website_id
                || ((r.data.available_websites || []).length === 1
                    ? r.data.available_websites[0].website_id
                    : null);
            setWebsiteId(initial);
            // Initial mount fires before username is typed — no auto-history.
            setAutoSelectedFromHistory(false);
        }).catch((e) => setErr(formatDetail(e?.response?.data?.detail) || "Link not found"));
    }, [linkCode]);

    // Shared auto-suggest call. Re-fetches /pay/{code}?username=X and, if
    // the backend returns a `suggested_website_id`, adopts it as the
    // customer's pick (unless they've touched the chooser manually).
    // Extracted so we can call it from BOTH the debounced effect (typing)
    // AND the onBlur handler (faster response when the user tabs out).
    const runAutoSelect = async () => {
        if (!info || websiteTouched || !username
            || (info.available_websites || []).length <= 1) return;
        try {
            const r = await api.get(`/public/pay/${linkCode}`, { params: { username } });
            if (r.data.suggested_website_id) {
                setWebsiteId(r.data.suggested_website_id);
                setAutoSelectedFromHistory(true);
            } else {
                // No history match — clear the auto-banner so it doesn't
                // linger from a stale value when the customer changes
                // username to one without prior payments.
                setAutoSelectedFromHistory(false);
            }
        } catch { /* non-fatal */ }
    };

    // Username-based auto-suggest: typed-debounce path. Faster response
    // (200ms after the user stops typing — was 400, but feedback was
    // "feels slow on iPhone"). The same call fires on blur for instant
    // adoption when the user tabs out of the field.
    useEffect(() => {
        if (!info || websiteTouched || !username || (info.available_websites || []).length <= 1) return;
        const t = setTimeout(runAutoSelect, 200);
        return () => clearTimeout(t);
         
    }, [username, info, websiteTouched, linkCode]);

    // Re-evaluate methods whenever amount or username changes
    useEffect(() => {
        const a = parseFloat(amount);
        if (!a || !username || !info?.available) { setMethods(null); setSelected(null); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.post(`/public/pay/${linkCode}/methods`, { amount: a, username });
                setMethods(r.data);
                // auto-select if only one available
                if (r.data.upi_available && !r.data.bank_available) setSelected("upi");
                else if (!r.data.upi_available && r.data.bank_available) setSelected("bank");
                else setSelected(null);
            } catch { setMethods(null); }
        }, 250);
        return () => clearTimeout(t);
    }, [amount, username, linkCode, info?.available]);

    const submit = async (e) => {
        e.preventDefault();
        if (!selected) { setErr("Please select a payment method."); return; }
        setErr(""); setBusy(true);
        try {
            const r = await api.post(`/public/pay/${linkCode}/initiate`, {
                amount: parseFloat(amount), username, method_preference: selected,
                website_id: websiteId || undefined,
                // Forward the embed sid set by embed.js (via querystring
                // or sessionStorage). Best-effort — null is fine.
                embed_sid: (params.get("pg_sid")
                    || (typeof window !== "undefined"
                        && window.sessionStorage
                        && window.sessionStorage.getItem("pg_sid"))
                    || undefined),
            });
            if (!r.data.ok) { setErr(r.data.reason || "Unable to assign a method right now."); setBusy(false); return; }
            nav(`/payment/${r.data.transaction_id}`);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
            setBusy(false);
        }
    };

    if (err && !info) return (
        <div className="public-bg min-h-screen flex items-center justify-center px-4">
            <div className="card p-8 max-w-md w-full text-center">
                <div className="text-3xl mb-2">😕</div>
                <div className="font-semibold">{err}</div>
            </div>
        </div>
    );
    if (!info) return <div className="public-bg min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

    const a = parseFloat(amount || 0);
    const inRange = a >= (info.min_amount || 0) && a <= (info.max_amount || 1e12);
    const showMethods = inRange && username && methods;
    const noEligible = showMethods && !methods.upi_available && !methods.bank_available;

    return (
        <div className="public-bg min-h-screen flex flex-col">
            <div className="px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">P</div>
                    <span className="font-bold tracking-tight">PayGateway</span>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Secure payment</div>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 pb-10">
                <div className="w-full max-w-md">
                    <div className="text-center mb-6">
                        {info.partner.logo_data_url && (
                            <img src={info.partner.logo_data_url} alt={info.partner.business_name}
                                 className="mx-auto mb-3 w-20 h-20 rounded-xl object-contain bg-white shadow-sm ring-1 ring-slate-200 p-1.5" />
                        )}
                        <div className="text-xs uppercase tracking-wider text-slate-500">You&apos;re paying</div>
                        <h1 className="text-2xl font-bold tracking-tight mt-1">{info.partner.business_name}</h1>
                    </div>

                    <form onSubmit={submit} className="card p-5 sm:p-6 space-y-4 sm:space-y-5">
                        <div>
                            <label className="label">Amount (₹)</label>
                            <input data-testid="public-amount" required
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="off"
                                value={amount}
                                onChange={(e) => {
                                    // Strip everything except digits — opens numeric
                                    // keyboard on mobile and rejects "e", "+", "-", "."
                                    // and any pasted alpha characters.
                                    const v = (e.target.value || "").replace(/[^0-9]/g, "");
                                    setAmount(v);
                                }}
                                className="input font-mono text-2xl text-center py-3" placeholder="0" autoFocus />
                            <QuickAmountChips
                                values={quickChipValues(info.min_amount, info.max_amount)}
                                current={amount}
                                onPick={(v) => setAmount(String(v))}
                            />
                            {info.min_amount != null && (
                                <div className="text-[11px] text-slate-500 mt-1.5 text-center">
                                    Range: <span className="font-mono">{fmtINR(info.min_amount)} – {fmtINR(info.max_amount)}</span>
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="label">Username / order id</label>
                            <input data-testid="public-username" required
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                onBlur={runAutoSelect}
                                className="input" placeholder="your_username" />
                        </div>

                        {/* Website chooser — shown only when 2+ websites are
                            configured for this link. Auto-suggested by the
                            backend when the username has a prior successful
                            payment for a specific website. Single-website
                            links pre-select silently (chooser hidden). */}
                        {(info.available_websites || []).length >= 2 && (
                            <div data-testid="website-chooser">
                                <label className="label flex items-center gap-1.5">
                                    <Globe className="w-3.5 h-3.5 text-slate-500" />
                                    Which website are you paying for?
                                </label>
                                {/* Auto-selected banner — surfaces only when the
                                    customer's username matched a prior payment.
                                    Critical UX cue: the customer sees a tile is
                                    pre-selected without knowing why; this banner
                                    answers the implicit "why is this one
                                    highlighted?" question. Hidden as soon as
                                    they manually pick a different website. */}
                                {autoSelectedFromHistory && !websiteTouched && (
                                    <div data-testid="website-auto-banner"
                                         className="mb-2 mt-1 px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded text-[11px] text-emerald-800 flex items-center gap-1.5">
                                        <Check className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                                        <span>Auto-selected based on your previous payments. Change below if needed.</span>
                                    </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
                                    {info.available_websites.map((w) => {
                                        const active = websiteId === w.website_id;
                                        const isSuggested = info.suggested_website_id === w.website_id && !websiteTouched;
                                        return (
                                            <button
                                                key={w.website_id}
                                                type="button"
                                                data-testid={`website-pick-${w.website_id}`}
                                                onClick={() => { setWebsiteId(w.website_id); setWebsiteTouched(true); setAutoSelectedFromHistory(false); }}
                                                className={`relative rounded-lg border-2 px-3 py-2.5 text-left text-sm transition-all ${
                                                    active
                                                        ? "border-blue-600 bg-blue-50 shadow-sm"
                                                        : "border-slate-200 bg-white hover:border-slate-300"
                                                }`}
                                            >
                                                <div className={`font-semibold truncate ${active ? "text-blue-900" : "text-slate-900"}`}>{w.name}</div>
                                                {isSuggested && (
                                                    <div className="text-[10px] text-emerald-700 mt-0.5 font-medium">✓ Suggested for you</div>
                                                )}
                                                {active && (
                                                    <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {showMethods && !noEligible && methods.upi_available && methods.bank_available && (
                            <div data-testid="payment-methods">
                                <label className="label">Payment Method</label>
                                <div className="grid grid-cols-2 gap-3 mt-1.5">
                                    <MethodTile
                                        testId="method-upi"
                                        active={selected === "upi"}
                                        disabled={!methods.upi_available}
                                        icon={Smartphone}
                                        label="UPI"
                                        sub={methods.upi_available ? `${methods.upi_count} available` : "Not available"}
                                        onClick={() => setSelected("upi")}
                                    />
                                    <MethodTile
                                        testId="method-bank"
                                        active={selected === "bank"}
                                        disabled={!methods.bank_available}
                                        icon={Building2}
                                        label="Bank Transfer"
                                        sub={methods.bank_available ? `${methods.bank_count} available` : "Not available"}
                                        onClick={() => setSelected("bank")}
                                    />
                                </div>
                            </div>
                        )}

                        {noEligible && (
                            <div data-testid="no-method" className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 text-center">
                                No payment method available for this amount right now. Try a different amount.
                            </div>
                        )}

                        {err && <div data-testid="public-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2.5">{err}</div>}

                        <button data-testid="public-pay-btn" type="submit" disabled={busy || !amount || !username || !info.available || !selected}
                            className="btn-primary w-full py-3 rounded-md font-medium text-base flex items-center justify-center gap-2">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            {selected ? `Continue with ${selected === "upi" ? "UPI" : "Bank Transfer"}` : "Continue to pay"}
                        </button>
                    </form>

                    <div className="text-[11px] text-center text-slate-400 mt-4 flex items-center justify-center gap-1.5">
                        <ShieldCheck className="w-3 h-3 text-emerald-500" /> 256-bit SSL encryption
                    </div>
                </div>
            </div>
        </div>
    );
}

function QuickAmountChips({ values, current, onPick }) {
    if (!values?.length) return null;
    return (
        <div className="flex flex-wrap gap-1.5 justify-center mt-2.5" data-testid="quick-amounts">
            {values.map((v) => {
                const active = String(current) === String(v);
                return (
                    <button type="button" key={v} data-testid={`quick-${v}`}
                            onClick={() => onPick(v)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                                active
                                    ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                    : "bg-white text-slate-700 border-slate-300 hover:border-blue-400 hover:bg-blue-50"
                            }`}>
                        ₹{v.toLocaleString("en-IN")}
                    </button>
                );
            })}
        </div>
    );
}

/** Standard quick-amount chips, intersected with the link's min/max so we
 *  never show a chip the partner can't accept. */
function quickChipValues(min, max) {
    const STD = [500, 1000, 2000, 5000, 10000];
    const lo = Number(min) || 0;
    const hi = Number(max) || 1e12;
    return STD.filter((v) => v >= lo && v <= hi);
}

function MethodTile({ icon: Icon, label, sub, active, disabled, onClick, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            className={`relative rounded-xl border-2 p-4 text-left transition-all ${
                disabled
                    ? "border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed"
                    : active
                        ? "border-blue-600 bg-blue-50 shadow-md"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
            }`}
        >
            <Icon className={`w-7 h-7 mb-2 ${active ? "text-blue-700" : "text-slate-700"}`} />
            <div className={`font-semibold text-base ${active ? "text-blue-900" : "text-slate-900"}`}>{label}</div>
            <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
            {active && (
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                </div>
            )}
        </button>
    );
}
